import { useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Header } from './Header'
import { Footer } from './Footer'
import { SupportWidgets } from './SupportWidgets'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { findRouteKeyFromPath, languageFromPath } from '@/i18n/routes'
import { contentRoutes } from '@/i18n/content-routes'
import { publicProducts } from '@/data/public-catalog'

function PreviewMetadata({ title, description }: { title: string; description: string }) {
  const { t } = useTranslation('common')
  useEffect(() => {
    document.title = t('shell.metaTitle', { title })
    const changes: { node: HTMLMetaElement; old: string | null; created: boolean }[] = []
    for (const [name, content] of [['robots', 'noindex, nofollow'], ['description', description]]) {
      let node = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
      const created = !node
      if (!node) { node = document.createElement('meta'); node.name = name; document.head.append(node) }
      changes.push({ node, old: node.getAttribute('content'), created })
      node.content = content
    }
    return () => changes.forEach(({ node, old, created }) => { if (created) node.remove(); else if (old === null) node.removeAttribute('content'); else node.content = old })
  }, [title, description, t])
  return null
}

export function PublicShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common')
  return <>
    <LocalizedLink to="#main-content" className="skip-link">{t('aria.skipToContent')}</LocalizedLink>
    <Header />
    <main id="main-content" tabIndex={-1} className="site-main">{children}</main>
    <SupportWidgets />
    <Footer />
  </>
}

export function ShellPlaceholder() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const lang = languageFromPath(location.pathname)
  const rest = location.pathname.replace(/\/$/, '').split('/').slice(2).join('/')
  const key = findRouteKeyFromPath(rest, lang)
  const record = contentRoutes.find((item) => item.type !== 'news' && item[lang] === location.pathname.replace(/\/$/, ''))
  if (!key && !record) return <NotFound />
  const product = record?.type === 'product' ? publicProducts.find((item) => item.id === record.id) : null
  const productTitle = product?.languages[lang]?.title ?? product?.languages.it?.title ?? product?.languages.en?.title
  const title = record?.type === 'category' ? t(`categories.${record.id}`) : productTitle ? t('shell.contentTitle', { title: productTitle }) : t(`nav.${key}`)
  return <div className="shell-container shell-placeholder">
    <PreviewMetadata title={title} description={t('shell.previewDescription')} />
    <h1 tabIndex={-1}>{key === 'home' ? t('brand.name') : title}</h1>
    <p className="placeholder-note">{t('shell.previewDescription')}</p>
    {product && !product.languages[lang] && <p>{t('shell.sourceFallback')}</p>}
    <div className="button-row"><LocalizedLink to="solutions" className="button">{t('nav.allSolutions')}</LocalizedLink><LocalizedLink to="contacts" className="button button-secondary">{t('buttons.contactUs')}</LocalizedLink></div>
    {key === 'home' && <section className="company-anchor"><h2 id="chi-siamo" tabIndex={-1}>{t('nav.about')}</h2><p>{t('shell.companyPlaceholder')}</p></section>}
  </div>
}

export function NotFound() {
  const { t } = useTranslation('common')
  return <div className="shell-container shell-placeholder">
    <PreviewMetadata title={t('notFound.title')} description={t('notFound.description')} />
    <h1 tabIndex={-1}>{t('notFound.title')}</h1><p>{t('notFound.description')}</p>
    <div className="button-row"><LocalizedLink to="home" className="button">{t('buttons.backToHome')}</LocalizedLink><LocalizedLink to="solutions" className="button button-secondary">{t('nav.allSolutions')}</LocalizedLink></div>
  </div>
}

export function ReservedShell() {
  const { t } = useTranslation('common')
  return <div className="reserved-shell">
    <PreviewMetadata title={t('nav.reserved')} description={t('reserved.description')} />
    <LocalizedLink to="#main-content" className="skip-link">{t('aria.skipToContent')}</LocalizedLink>
    <header className="shell-container reserved-header"><LocalizedLink to="home" className="brand-link" aria-label={t('aria.brandHome')}><img src="/brand/esamed.png" width="230" height="100" alt={t('brand.name')} /></LocalizedLink><LocalizedLink to="home" className="button button-secondary">{t('reserved.returnToSite')}</LocalizedLink><LanguageSelector /></header>
    <div className="demo-notice"><p className="shell-container">{t('reserved.notice')}</p></div>
    <main id="main-content" tabIndex={-1} className="shell-container shell-placeholder"><h1 tabIndex={-1}>{t('nav.reserved')}</h1><p>{t('reserved.description')}</p><p className="placeholder-note">{t('reserved.placeholder')}</p></main>
  </div>
}
